import React, { lazy } from 'react';
import { AdvertorialHero } from './components/AdvertorialHero';
import { StorySection } from './components/StorySection';
import { ProductSticky } from './components/ProductSticky';
import { TestimonialCard } from './components/TestimonialCard';
import { CTAButton } from './components/CTAButton';
import { Disclosure } from './components/Disclosure';
import { ShieldCheck, Truck, Lock } from 'lucide-react';
export function App() {
  return <div className="min-h-screen bg-white" data-id="element-0">
      {/* Navigation Bar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-1">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between" data-id="element-2">
          <div className="font-serif font-bold text-xl text-gray-900" data-id="element-3">
            Health<span className="text-[#7C3AED]" data-id="element-4">Daily</span>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12" data-id="element-5">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-6">
          {/* Main Content Column */}
          <article className="flex-1 max-w-[700px] mx-auto lg:mx-0" data-id="element-7">
            <AdvertorialHero data-id="element-8" />

            <StorySection imageSrc="https://cdn.magicpatterns.com/uploads/cr36taUm6ZLPdAaUGZpfxW/hf_20260206_191747_e4c8f52b-3ee3-4653-b38b-6079b4565d48.png" imageAlt="Older man smiling outdoors" imageCaption="I noticed he started covering his mouth every time he laughed." data-id="element-9">
              <p data-id="element-10">My father was 58 when he lost his third tooth.</p>
              <p data-id="element-11">
                I remember sitting across from him at Thanksgiving dinner,
                watching him carefully navigate around the corn on the cob. He
                used to love corn on the cob—he'd hold it with both hands and go
                at it like a typewriter, butter dripping down his chin, not
                caring who saw.
              </p>
              <p data-id="element-12">
                Now he just stared at it. Then quietly asked my mom to cut it
                off for him.
              </p>
              <p data-id="element-13">
                My nephew—he was maybe seven at the time—looked up and said,
                "Grandpa, why can't you eat it like normal?"
              </p>
              <p data-id="element-14">
                The table went silent. My dad forced a smile and said, "Just
                getting old, buddy."
              </p>
              <p data-id="element-15">
                But I saw his jaw tighten. I saw him push the corn to the side
                of his plate and not touch it again.
              </p>
              <p data-id="element-16">He tried to laugh it off. "Getting old," he said.</p>
              <p data-id="element-17">But I noticed things.</p>
              <p data-id="element-18">
                The way he started covering his mouth with his hand every time
                he laughed—like he was coughing instead of smiling.
              </p>
              <p data-id="element-19">
                The way he quietly suggested "somewhere else" whenever someone
                picked a restaurant with ribs or wings or anything that required
                real chewing.
              </p>
              <p data-id="element-20">
                The way he ducked out of family photos. Or stood in the back row
                with his lips pressed tight together, this thin, closed-mouth
                smile that didn't look anything like him.
              </p>
              <p data-id="element-21">
                My dad used to have this big, wide, genuine grin. The kind where
                you could see every tooth. The kind that made you feel like
                everything was going to be okay.
              </p>
              <p data-id="element-22">I hadn't seen that smile in years.</p>
            </StorySection>

            <StorySection data-id="element-23">
              <p data-id="element-24">My dad wasn't old. He wasn't careless.</p>
              <p data-id="element-25">
                He brushed twice a day, every day, for as long as I could
                remember. He had this whole routine—soft-bristle brush, two
                minutes on the dot, angled at 45 degrees like the dentist told
                him. He even replaced his toothbrush every three months like
                clockwork.
              </p>
              <p data-id="element-26">But flossing? That was a different story.</p>
              <p data-id="element-27">
                "My gums bleed every time," he used to say. "I've tried
                everything. The regular stuff, the waxed kind, those little
                plastic picks. Doesn't matter. It hurts too much."
              </p>
              <p data-id="element-28">
                He'd shake his head. "I do good for a week, maybe two. Then I
                just... can't anymore."
              </p>
              <p data-id="element-29">Sound familiar?</p>
              <p data-id="element-30">
                I'm 42 now. And for the past few years, every time I've looked
                in the mirror after brushing—every time I've seen that pink
                tinge swirling in the sink—I've thought about my dad.
              </p>
              <p data-id="element-31">
                I'd run my tongue along my gum line. Press on a tooth to see if
                it felt loose. Tell myself I was being paranoid.
              </p>
              <p data-id="element-32">But late at night, the questions would creep in.</p>
              <p data-id="element-33">Was I already on the same path?</p>
              <p data-id="element-34">
                Would I be the one avoiding photos at my kid's graduation?
                Cutting my steak into tiny pieces at my own retirement dinner?
                Quietly Googling "how much do dental implants cost" at 2am?
              </p>
              <p data-id="element-35">Was I going to lose my smile too?</p>
            </StorySection>

            <StorySection title="The Dentist Visit That Changed Everything" imageSrc="https://cdn.magicpatterns.com/uploads/5ZWWxjW5s5MC6iYg4BHMrU/hf_20260204_020333_b61d8c8c-04da-41e5-8ece-af9abf224f49.png" imageAlt="Dr. Patel examining patient" data-id="element-36">
              <p data-id="element-37">
                Six months ago, I sat in my dentist's chair for a routine
                cleaning.
              </p>
              <p data-id="element-38">
                You know that feeling—the paper bib clipped around your neck,
                the bright light in your face, the taste of latex gloves and
                that sharp metal pick scraping along your teeth.
              </p>
              <p data-id="element-39">
                Dr. Patel poked around. Made some notes on her little tablet.
                Poked some more.
              </p>
              <p data-id="element-40">
                Then she set down her tools, pulled off her gloves, and said the
                words I'd been dreading:
              </p>
              <p className="font-bold bg-yellow-50 p-4 border-l-4 border-yellow-400 rounded-r" data-id="element-41">
                "Your gums are inflamed. We're seeing some recession in a few
                areas. If you don't change something soon, we're going to be
                talking about deep cleanings—or worse."
              </p>
              <p data-id="element-42">I felt my face get hot.</p>
              <p data-id="element-43">Here came the lecture.</p>
              <p data-id="element-44">"Are you flossing regularly?"</p>
              <p data-id="element-45">
                I wanted to lie. I really did. But what was the point? She could
                see my gums. She knew.
              </p>
              <p data-id="element-46">
                "I try," I said, not meeting her eyes. "But every time I floss,
                my gums bleed. It hurts. I do good for a week, maybe two, and
                then I just... stop."
              </p>
              <p data-id="element-47">
                I braced myself. I knew what was coming. The disappointed head
                shake. The "you really need to take better care of yourself"
                speech. The same guilt trip I'd been giving myself for years.
              </p>
              <p data-id="element-48">
                But instead, she nodded slowly. Not in judgment—more like
                recognition.
              </p>
              <p data-id="element-49">
                "You're not alone," she said. "I hear that exact thing from
                about half my patients."
              </p>
              <p data-id="element-50">Half?</p>
              <p data-id="element-51">I looked up.</p>
            </StorySection>

            <StorySection title='"What If It&#39;s Not Your Fault?"' data-id="element-52">
              <p data-id="element-53">Then she said something I didn't expect.</p>
              <p data-id="element-54">
                She leaned back in her chair, looked at me directly, and asked:
              </p>
              <p className="font-serif text-xl italic text-gray-900" data-id="element-55">
                "What if it's not your fault?"
              </p>
              <p data-id="element-56">I blinked. "What do you mean?"</p>
              <p data-id="element-57">
                Instead of answering, Dr. Patel swiveled her monitor toward me
                and pulled up a diagram. It showed a cross-section of
                gums—healthy gums versus inflamed gums.
              </p>
              <p data-id="element-58">
                "Here's what most people don't understand," she said. "When your
                gums are already inflamed—which yours are—string floss can
                actually make things worse."
              </p>
              <p data-id="element-59">I leaned forward. "Wait, what?"</p>
              <p data-id="element-60">
                "Think about it," she continued. "String floss works by
                scraping. You're running a thin piece of string between your
                teeth and sawing it back and forth against your gum tissue. If
                that tissue is healthy, no problem. But if it's already inflamed
                and sensitive—which, by the way, studies suggest is true for
                nearly half of adults over 30?"
              </p>
              <p data-id="element-61">She paused.</p>
              <p data-id="element-62">
                "You're essentially scraping an open wound. You create
                micro-tears in the tissue. Those micro-tears get exposed to
                bacteria. The bacteria causes more inflammation. More
                inflammation causes more bleeding. More bleeding causes more
                pain. So you avoid flossing. And the cycle continues."
              </p>
              <p data-id="element-63">She drew a little circle in the air with her finger.</p>
              <p data-id="element-64">
                "Round and round. Year after year. And it's not because you're
                lazy or undisciplined. It's because the tool you've been told to
                use is actually working against you."
              </p>
              <p data-id="element-65">I sat there, stunned.</p>
              <p data-id="element-66">For years—YEARS—I'd blamed myself.</p>
              <p data-id="element-67">
                I thought I was lazy. Undisciplined. Another person who couldn't
                follow through on the simple things.
              </p>
              <p data-id="element-68">
                I'd lie in bed some nights thinking about it. Beating myself up.
                Promising myself I'd be better tomorrow. Then failing again.
                Feeling that familiar shame every time I saw blood in the sink.
              </p>
              <p data-id="element-69">
                But sitting there in that dentist's chair, something shifted.
              </p>
              <p data-id="element-70">The problem wasn't me.</p>
              <p data-id="element-71">The problem was the tool.</p>
              <p data-id="element-72">
                Dr. Patel put it simply: "You wouldn't scrub an open wound with
                string. So why are we scraping inflamed gums with floss?"
              </p>
              <p data-id="element-73">String floss was invented in 1815. Over 200 years ago.</p>
              <p data-id="element-74">
                Think about that. We've completely reinvented how we
                communicate, how we travel, how we treat disease. We've put
                robots on Mars.
              </p>
              <p data-id="element-75">
                But the tool most dentists still recommend for gum health?
                Essentially the same piece of string they used when James
                Madison was president.
              </p>
              <p data-id="element-76">
                Meanwhile, we've learned so much more about gum health,
                inflammation, and what actually works for sensitive tissue.
                Peer-reviewed research. Clinical trials. Real science.
              </p>
              <p data-id="element-77">And here's what that science tells us:</p>
              <p className="font-bold text-gray-900" data-id="element-78">
                The bleeding I'd been experiencing for years? It wasn't proof
                that flossing was working.
              </p>
              <p className="font-bold text-gray-900" data-id="element-79">
                It was proof of mechanical trauma.
              </p>
              <p data-id="element-80">It was my gums screaming for me to stop.</p>
            </StorySection>

            <StorySection title="A Different Approach" imageSrc="https://cdn.magicpatterns.com/uploads/oeXDS2UHpc4Ng1pDF1yEWR/hf_20260204_022723_4d51b387-066b-4261-9454-de32f179ad6b.png" imageAlt="Woman flossing her teeth" data-id="element-81">
              <p data-id="element-82">
                "So what am I supposed to do?" I asked. "Just... not floss? Let
                my gums get worse?"
              </p>
              <p data-id="element-83">Dr. Patel smiled. "No. There's another way."</p>
              <p data-id="element-84">
                She explained that for patients with sensitive or inflamed gums,
                she'd been recommending something gentler than string floss:
                water flossing.
              </p>
              <p data-id="element-85">
                "Water doesn't saw," she said. "It flows. It flushes. It cleans
                below the gumline without mechanical abrasion."
              </p>
              <p data-id="element-86">Here's how she explained it:</p>
              <p data-id="element-87">
                String floss scrapes at the gumline. It pushes bacteria
                around—and into micro-wounds it creates.
              </p>
              <p data-id="element-88">
                A water flosser uses pulsed water pressure to flush bacteria out
                of the gum pockets. It reaches places string floss physically
                can't. And it does it without scraping, cutting, or tearing
                sensitive tissue.
              </p>
              <p data-id="element-89">
                "The result," she said, "is that your gums can actually heal
                while you're still cleaning effectively. No more pain-avoidance
                cycle."
              </p>
              <p data-id="element-90">
                I'd heard of water flossers before. I'd even looked at those big
                countertop Waterpik machines. But my bathroom is tiny. I have
                zero counter space. And honestly, they looked complicated and
                messy.
              </p>
              <p data-id="element-91">
                "Those aren't your only option anymore," Dr. Patel said. "There
                are cordless ones now that are actually good. Small, portable,
                effective. Find one you'll actually use—that's what matters."
              </p>
            </StorySection>

            <StorySection title="What I Was Looking For" data-id="element-92">
              <p data-id="element-93">
                That night, I went down a rabbit hole. I read reviews. Compared
                products. Watched YouTube videos of people testing different
                water flossers.
              </p>
              <p data-id="element-94">Here's what I learned:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-95">
                <li data-id="element-96">
                  Countertop water flossers (like the classic Waterpik) work
                  well, but they're bulky, messy, and most people abandon them
                  within a few months because they're inconvenient.
                </li>
                <li data-id="element-97">
                  Cheap cordless flossers have weak pressure that doesn't
                  actually clean effectively, and their batteries die after a
                  few uses.
                </li>
              </ul>
              <p data-id="element-98">I needed something different. Something that was:</p>
              <ul className="space-y-2 mb-4" data-id="element-99">
                <li className="flex items-start gap-2" data-id="element-100">
                  <span className="text-[#7C3AED] font-bold" data-id="element-101">✓</span>
                  <span data-id="element-102">
                    <strong data-id="element-103">Portable</strong> – small enough for my tiny
                    bathroom (and travel)
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-104">
                  <span className="text-[#7C3AED] font-bold" data-id="element-105">✓</span>
                  <span data-id="element-106">
                    <strong data-id="element-107">Powerful enough to work</strong> – not some weak toy
                    that just moves water around
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-108">
                  <span className="text-[#7C3AED] font-bold" data-id="element-109">✓</span>
                  <span data-id="element-110">
                    <strong data-id="element-111">Gentle enough to use daily</strong> – I didn't want
                    to trade one painful routine for another
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-112">
                  <span className="text-[#7C3AED] font-bold" data-id="element-113">✓</span>
                  <span data-id="element-114">
                    <strong data-id="element-115">Built for consistency</strong> – simple enough that
                    I'd actually stick with it
                  </span>
                </li>
              </ul>
              <p data-id="element-116">That's when I found The DeepClean Flow.</p>
            </StorySection>

            <StorySection title="The DeepClean Flow" imageSrc="https://cdn.magicpatterns.com/uploads/5M8ftuGo7gWZqacAabLtgn/openart-image_N2BGEawH_1769469296519_raw_(1).jpg" imageAlt="DeepClean Flow water flosser" data-id="element-117">
              <p data-id="element-118">
                The DeepClean Flow is a rechargeable cordless water flosser made
                by a company called FlossFocus. Here's what caught my attention:
              </p>
              <p data-id="element-119">
                <strong data-id="element-120">It's small.</strong> Like, actually small. It fits in my
                bathroom drawer. It fits in my travel bag. No cords, no bulky
                reservoir taking up counter space.
              </p>
              <p data-id="element-121">
                <strong data-id="element-122">It's powerful.</strong> The water pressure is calibrated
                to be effective—you can feel it working—but it's not painful.
                It's firm, not aggressive.
              </p>
              <p data-id="element-123">
                <strong data-id="element-124">The battery actually lasts.</strong> I charge it maybe
                once every two weeks. No more dead devices when I need them.
              </p>
              <p data-id="element-125">
                And the whole routine takes about 60 seconds. Fill it, point,
                done.
              </p>
              <p data-id="element-126">
                But honestly? What sold me was this: it was designed for people
                like me. People who've tried and failed with string floss.
                People who need something gentle enough to actually use, but
                effective enough to actually work.
              </p>
              <p data-id="element-127">People who don't need another thing to feel guilty about.</p>
            </StorySection>

            <StorySection title="What Happened When I Tried It" data-id="element-128">
              <p data-id="element-129">
                I'll be honest: the first few days felt weird. Water shooting
                between my teeth. A little messy until I figured out the angle.
                My gums still bled a bit.
              </p>
              <p data-id="element-130">But here's the difference: it didn't hurt.</p>
              <p data-id="element-131">No sawing. No wincing. No dreading it.</p>
              <div className="bg-blue-50 p-6 rounded-lg my-6 space-y-4" data-id="element-132">
                <div className="flex gap-4" data-id="element-133">
                  <div className="font-bold text-[#7C3AED] whitespace-nowrap" data-id="element-134">
                    Week 1:
                  </div>
                  <div data-id="element-135">
                    Still some bleeding, but noticeably less than with string
                    floss. I actually looked forward to that clean feeling
                    afterward.
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-136">
                  <div className="font-bold text-[#7C3AED] whitespace-nowrap" data-id="element-137">
                    Week 2:
                  </div>
                  <div data-id="element-138">
                    The bleeding almost completely stopped. For the first time
                    in years, my gums didn't look angry and red.
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-139">
                  <div className="font-bold text-[#7C3AED] whitespace-nowrap" data-id="element-140">
                    Week 4:
                  </div>
                  <div data-id="element-141">
                    I went back to Dr. Patel for a follow-up. She looked at my
                    gums, paused, and said: "Whatever you're doing, keep doing
                    it. This is real improvement."
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-142">
                  <div className="font-bold text-[#7C3AED] whitespace-nowrap" data-id="element-143">
                    Month 3:
                  </div>
                  <div data-id="element-144">
                    Regular checkup. No lecture. No warnings. She actually
                    smiled and said my gums looked healthier than they had in
                    years.
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-145">
                  <div className="font-bold text-[#7C3AED] whitespace-nowrap" data-id="element-146">
                    Month 6 (now):
                  </div>
                  <div data-id="element-147">
                    I've used it every single night. Not because I'm
                    disciplined—because it's easy. Because it doesn't hurt.
                    Because it takes 60 seconds and I feel genuinely clean
                    afterward.
                  </div>
                </div>
              </div>
              <p className="font-bold text-xl text-center my-8" data-id="element-148">
                This is the first oral care habit, besides brushing, that has
                ever actually stuck.
              </p>
            </StorySection>

            <StorySection title="I'm Not the Only One" data-id="element-149">
              <p data-id="element-150">
                After my experience, I started telling friends. Then I looked up
                reviews online. Turns out, a lot of people have had similar
                journeys:
              </p>

              <TestimonialCard quote="I've bought every dental gadget and quit every single one. This is the first thing that actually stuck. My dentist asked what I changed—she couldn't believe my gum measurements. Six months and counting." author="Michael" age="42" delay={0.1} data-id="element-151" />

              <TestimonialCard quote="My gums bled every single time I flossed. I thought that was just normal for me. Two weeks with the DeepClean Flow and the bleeding stopped. I didn't know that was even possible. I wish someone had told me about this years ago." author="Sarah" age="37" delay={0.2} data-id="element-152" />

              <TestimonialCard quote="My hygienist asked what I'd changed. When I told her, she said she recommends water flossers to all her patients with sensitive gums now. Why didn't anyone tell me this sooner?" author="David" age="51" delay={0.3} data-id="element-153" />

              <TestimonialCard quote="My mom has dentures at 65. I refuse to let that happen to me. The DeepClean Flow is the easiest thing I do for my health every day. It's become as automatic as brushing." author="Jennifer" age="44" delay={0.4} data-id="element-154" />
            </StorySection>

            <StorySection title="What I Wish I'd Known Sooner" data-id="element-155">
              <p data-id="element-156">
                Before I tried it, I had a lot of the same questions you
                probably have:
              </p>

              <div className="space-y-6 mt-6" data-id="element-157">
                <div data-id="element-158">
                  <h4 className="font-bold text-gray-900 mb-2" data-id="element-159">
                    "Isn't string floss good enough?"
                  </h4>
                  <p data-id="element-160">
                    If you can use it consistently without pain or
                    bleeding—great. But if your gums are already inflamed,
                    string floss can make things worse. Water flossing cleans
                    without the mechanical trauma.
                  </p>
                </div>

                <div data-id="element-161">
                  <h4 className="font-bold text-gray-900 mb-2" data-id="element-162">
                    "Aren't water flossers messy?"
                  </h4>
                  <p data-id="element-163">
                    The countertop ones can be. The DeepClean Flow is designed
                    to be used over the sink with minimal splash. It took me
                    about two days to get the angle right. Now it's cleaner than
                    dealing with string floss.
                  </p>
                </div>

                <div data-id="element-164">
                  <h4 className="font-bold text-gray-900 mb-2" data-id="element-165">
                    "It's more expensive than a pack of floss."
                  </h4>
                  <p data-id="element-166">
                    True. But here's the math that convinced me: A pack of floss
                    I never use costs infinite dollars per use. The DeepClean
                    Flow, used daily, costs pennies per day. And compare it to
                    the cost of one deep cleaning ($300-800), one filling
                    ($150-300), or one dental implant ($3,000-5,000). Prevention
                    is always cheaper than treatment.
                  </p>
                </div>

                <div data-id="element-167">
                  <h4 className="font-bold text-gray-900 mb-2" data-id="element-168">
                    "I'll probably just stop using it like everything else."
                  </h4>
                  <p data-id="element-169">
                    That was my fear too. But most gadgets fail because they're
                    painful, complicated, or inconvenient. The DeepClean Flow is
                    none of those things. 60 seconds. No pain. No setup. That's
                    why it actually sticks.
                  </p>
                </div>
              </div>
            </StorySection>

            <StorySection title="What I Think About Now" imageSrc="https://cdn.magicpatterns.com/uploads/7qpE7RMoJQJGg7kaUa7cdo/hf_20260205_010845_b06a31fa-850e-4abe-aa5d-b2d65b4e36d0.png" imageAlt="Grandfather reading with grandchildren" data-id="element-170">
              <p data-id="element-171">
                Every night, when I use my DeepClean Flow, I think about my dad.
              </p>
              <p data-id="element-172">
                He didn't lose his teeth overnight. It happened slowly. Years of
                bleeding he ignored. Warnings he dismissed. Small problems that
                became big ones.
              </p>
              <p data-id="element-173">
                By the time he realized how serious it was, the damage was done.
                Deep cleanings. Gum surgery. Extractions. Thousands of dollars
                and years of discomfort that he tells me—with regret in his
                eyes—could have been prevented.
              </p>
              <p data-id="element-174">
                "I wish I'd taken it more seriously earlier," he says now. "I
                thought bleeding was just normal. I thought I had time."
              </p>
              <p data-id="element-175">
                He's 67 now. He doesn't smile in photos. He avoids hard foods.
                He's embarrassed to eat in front of his grandchildren.
              </p>
              <p data-id="element-176">I'm not going to be him.</p>
              <p data-id="element-177">
                Not because I'm better. Not because I'm more disciplined. But
                because I finally found a tool that actually works for me.
              </p>
            </StorySection>

            <StorySection title="Two Paths" data-id="element-178">
              <p data-id="element-179">
                If you've read this far, you're probably in a similar place to
                where I was six months ago. Bleeding gums. Guilt about flossing.
                Worry about where this is all heading.
              </p>
              <p data-id="element-180">The way I see it, you have two choices:</p>

              <div className="grid md:grid-cols-2 gap-6 my-8" data-id="element-181">
                <div className="bg-red-50 p-6 rounded-lg border border-red-100" data-id="element-182">
                  <h4 className="font-bold text-red-800 mb-2" data-id="element-183">Path 1:</h4>
                  <p className="text-sm text-red-900" data-id="element-184">
                    Keep doing what you're doing. Keep trying string floss (or
                    avoiding it). Keep bleeding, keep feeling guilty, keep
                    dreading the dentist. Hope the damage isn't already too far
                    along. Watch the slow decline happen—just like my dad.
                  </p>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg border border-purple-100 shadow-sm" data-id="element-185">
                  <h4 className="font-bold text-purple-800 mb-2" data-id="element-186">Path 2:</h4>
                  <p className="text-sm text-purple-900" data-id="element-187">
                    Try something different. 60 seconds tonight. No pain, no
                    bleeding, no excuses. A routine you might actually stick
                    with. Gums that heal instead of getting worse. Dentist
                    visits without shame. A future where you get to keep your
                    teeth.
                  </p>
                </div>
              </div>

              <p data-id="element-188">I know which path I chose.</p>
              <p data-id="element-189">
                And every night, when I finish my 60-second routine and see no
                pink in the sink, I'm grateful I found another way.
              </p>
            </StorySection>

            <section className="bg-gray-50 rounded-xl p-8 border border-gray-200 text-center my-12" data-id="element-190">
              <h3 className="text-3xl font-bold font-serif mb-4" data-id="element-191">
                Ready to Try It?
              </h3>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-192">
                The DeepClean Flow comes with a 60-day money-back guarantee. Use
                it for two full months. If your gums don't feel healthier, if
                you're not using it every night, if it ends up in a drawer—you
                get a full refund. No questions, no hassle.
              </p>

              <p className="font-bold text-gray-900 mb-8" data-id="element-193">
                Your gums won't wait. But at least now you don't have to either.
              </p>

              <div className="flex justify-center mb-6" data-id="element-194">
                <CTAButton href="https://flossfocus.com/products/the-flossfocus-precision" className="text-xl px-12 py-5 shadow-xl hover:shadow-2xl" data-id="element-195">
                  Get The DeepClean Flow Now
                </CTAButton>
              </div>

              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500 font-medium" data-id="element-196">
                <div className="flex items-center gap-2" data-id="element-197">
                  <ShieldCheck className="w-5 h-5 text-[#7C3AED]" data-id="element-198" />
                  <span data-id="element-199">60-Day Money-Back Guarantee</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-200">
                  <Truck className="w-5 h-5 text-[#7C3AED]" data-id="element-201" />
                  <span data-id="element-202">Free Shipping</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-203">
                  <Lock className="w-5 h-5 text-[#7C3AED]" data-id="element-204" />
                  <span data-id="element-205">Secure Checkout</span>
                </div>
              </div>
            </section>
          </article>

          {/* Sidebar Column */}
          <div className="w-full lg:w-[300px] flex-shrink-0" data-id="element-206">
            <ProductSticky data-id="element-207" />
          </div>
        </div>
      </main>

      {/* Mobile Sticky CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-50" data-id="element-208">
        <CTAButton href="https://flossfocus.com/products/the-flossfocus-precision" fullWidth className="py-3 text-base" data-id="element-209">
          Get DeepClean Flow
        </CTAButton>
      </div>

      <Disclosure data-id="element-210" />
    </div>;
}